import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useId, useState, type FormEvent } from 'react';

import { Botao } from '@/componentes/Botao';
import { CabecalhoPagina } from '@/componentes/CabecalhoPagina';
import { Campo, CampoSelecao } from '@/componentes/Campo';
import { Barra, Carregando, ControleCarregando } from '@/componentes/Esqueleto';
import { EstadoVazio } from '@/componentes/EstadoVazio';
import { Medida } from '@/componentes/Medida';
import { DadoChave, PainelDetalhe } from '@/componentes/PainelDetalhe';
import { EsqueletoTabela, QuadroTabela, RodapeDeContagem, RodapeTabela, Tabela, TextoDaCelula, type Coluna } from '@/componentes/Tabela';
import { HistoriaNaMesa } from '@/historias/mesa';
import { formatarData, formatarDataHora, formatarDia, formatarInteiro } from '@/lib/formato';
import { explicarFalha } from '@/lib/pedido';
import { anunciar, retornoDe } from '@/lib/retorno';
/* O mesmo auxiliar de plural que o painel e os custos usam: 'contagem' faz o
   substantivo concordar com o numero (p.22), em vez do '(s)' que o manual
   proibe. Importar de outro modulo aqui segue o que 'custos' ja faz. */
import { usePaginacao } from '@/modulos/operacao/paginacao';
import { contagem } from '@/modulos/painel/painel';

import { api } from './api';
import {
  apiDaFolha,
  linhasParaConferir,
  mesCorrente,
  montarEnvio,
  ROTULO_DO_INSTRUMENTO,
  type AcertoDaApi,
  type Campo as CampoDaLinha,
  type FolhaDaApi,
  type Instrumento,
  type LeituraDaApi,
  type LinhaEmConferencia,
} from './folhas';
import { HubDoCombustivel } from './HubDoCombustivel';
import { AcoesNoRodape, JanelaComRodape } from './RodapeDaJanela';
import { opcaoDaMaquina } from '@/modulos/equipamentos/opcao-da-maquina';
import { useOpcoesComVisualDaFrota } from '@/modulos/equipamentos/opcoesComVisual';

/**
 * FOLHAS DE CAMPO (módulo 08; decisões 9, 10 e 12).
 *
 * O campo alimenta o sistema pela folha impressa. Aqui se emite a folha de uma
 * máquina, por um tanque, num período; se envia a imagem escaneada; e quem
 * escaneou confere o que a IA leu, campo a campo, olhando o papel ao lado,
 * antes de lançar. A taxa de acerto por instrumento (caneta ou lápis) fica no
 * topo: é o número que decide, como o dono pediu.
 */

const CHAVE = ['combustivel', 'folhas'] as const;
const dataCurta = (iso: string) => iso.split('-').reverse().join('/');
const ROTULO_DO_CAMPO: Readonly<Record<CampoDaLinha, string>> = {
  data: 'data',
  hora: 'hora',
  medidor: 'medidor',
  litros: 'litros',
  operador: 'operador',
};

export function PaginaFolhas() {
  const [emitindo, setEmitindo] = useState(false);
  const [selecionada, setSelecionada] = useState<string | null>(null);
  const [conferindo, setConferindo] = useState<{ folhaId: string; leituraId: string } | null>(null);
  const lista = useQuery({ queryKey: [...CHAVE, 'lista'], queryFn: () => apiDaFolha.folhas() });
  const paginaDasFolhas = usePaginacao(lista.data?.folhas ?? []);

  if (conferindo) {
    return (
      <div className="min-w-0 flex-1 overflow-auto">
        {/* `pagina`: as margens e o espaço entre blocos da R01 (32/24/16), e
            não um p-6 fixo em qualquer largura. */}
        <div className="pagina">
          <ConferenciaDaLeitura
            folhaId={conferindo.folhaId}
            leituraId={conferindo.leituraId}
            aoTerminar={() => {
              setConferindo(null);
            }}
          />
        </div>
      </div>
    );
  }

  const folha = lista.data?.folhas.find((f) => f.id === selecionada) ?? null;

  return (
    <div className="flex min-h-0 min-w-0 flex-1">
      <HubDoCombustivel>
        {/* "Emitir folha" é SECUNDÁRIA aqui: o laranja da tela é o "Lançar
          abastecimento" do hub. A emissão abre em janela. */}
        <div className="flex justify-end">
        <Botao
          nivel="secundario"
          onClick={() => {
            setEmitindo(true);
          }}
        >
          Emitir folha
        </Botao>
        </div>

        {lista.data && !lista.data.leituraLigada ? (
          <div role="status" className="flex flex-col gap-1 rounded-controle bg-informacao-fundo px-4 py-2">
            <span className="txt-apoio font-semibold text-informacao-texto">Leitura pela IA desligada neste ambiente</span>
            <span className="txt-apoio text-tinta-secundaria">
              A folha pode ser emitida e impressa, mas a imagem escaneada não é lida. Enquanto isso, lance pelo formulário.
            </span>
          </div>
        ) : null}

        <JanelaComRodape
          titulo="Emitir folha"
          aberta={emitindo}
          aoFechar={() => {
            setEmitindo(false);
          }}
        >
          <EmitirFolha
            aoFechar={() => {
              setEmitindo(false);
            }}
            aoEmitir={(f) => {
              setEmitindo(false);
              setSelecionada(f.id);
            }}
          />
        </JanelaComRodape>

        <AcertoPorInstrumento acerto={lista.data?.acerto ?? []} carregando={lista.isPending} />

        <div data-painel-origem>
          <Tabela
            rotulo="Folhas de campo emitidas"
            colunas={COLUNAS}
            linhas={paginaDasFolhas.visiveis}
            rodape={<RodapeTabela {...paginaDasFolhas.rodape} />}
            chaveDaLinha={(f) => f.id}
            carregando={lista.isPending}
            alturaDupla
            selecionada={selecionada}
            aoSelecionar={(f) => {
              setSelecionada(f.id);
            }}
            erro={
              lista.isError ? (
                <EstadoVazio
                  titulo="Não foi possível carregar as folhas"
                  descricao={explicarFalha(lista.error).comoResolver}
                  acao={
                    <Botao
                      nivel="secundario"
                      onClick={() => {
                        void lista.refetch();
                      }}
                    >
                      Carregar outra vez
                    </Botao>
                  }
                />
              ) : null
            }
            vazio={
              <EstadoVazio
                titulo="Nenhuma folha emitida"
                descricao="Emita a folha da primeira máquina: ela sai com a máquina, o tanque e o período impressos, e o campo só escreve o abastecimento."
                acao={
                  <Botao
                    nivel="secundario"
                    onClick={() => {
                      setEmitindo(true);
                    }}
                  >
                    Emitir folha
                  </Botao>
                }
              />
            }
          />
        </div>
      </HubDoCombustivel>

      {folha ? (
        <PainelDaFolha
          key={folha.id}
          folha={folha}
          leituraLigada={lista.data?.leituraLigada ?? false}
          aoConferir={(leituraId) => {
            setConferindo({ folhaId: folha.id, leituraId });
          }}
          aoFechar={() => {
            setSelecionada(null);
          }}
        />
      ) : null}
    </div>
  );
}

const COLUNAS: readonly Coluna<FolhaDaApi>[] = [
  { chave: 'codigo', rotulo: 'Folha', celula: (f) => <span className="codigo">{f.codigo}</span> },
  {
    chave: 'maquina',
    rotulo: 'Máquina',
    /* Código da máquina em monoespaçada, como em toda tabela (p.18). */
    celula: (f) => (
      <span className="flex min-w-0 flex-col">
        <span className="codigo text-tinta">{f.equipamentoCodigo}</span>
        <TextoDaCelula texto={f.equipamentoDescricao} classe="txt-apoio text-tinta-secundaria" linhas={1} />
      </span>
    ),
  },
  {
    chave: 'tanque',
    rotulo: 'Tanque',
    celula: (f) => (
      <span className="flex min-w-0 flex-col">
        <TextoDaCelula texto={f.tanqueNome} classe="txt-corpo text-tinta" linhas={1} />
        <TextoDaCelula texto={f.produtoNome} classe="txt-apoio text-tinta-secundaria" linhas={1} />
      </span>
    ),
  },
  /* Período de datas não quebra (p.18). */
  { chave: 'periodo', rotulo: 'Período', quebra: 'nunca', celula: (f) => `${dataCurta(f.de)} a ${dataCurta(f.ate)}` },
  { chave: 'leituras', rotulo: 'Leituras', alinhamento: 'numero', celula: (f) => formatarInteiro(f.leituras) },
  { chave: 'lancados', rotulo: 'Lançados', alinhamento: 'numero', celula: (f) => formatarInteiro(f.lancados) },
];

function AcertoPorInstrumento({
  acerto,
  carregando,
}: {
  readonly acerto: readonly AcertoDaApi[];
  readonly carregando: boolean;
}) {
  const colunas: readonly Coluna<AcertoDaApi>[] = [
    { chave: 'instrumento', rotulo: 'Instrumento', celula: (a) => ROTULO_DO_INSTRUMENTO[a.instrumento] },
    { chave: 'leituras', rotulo: 'Folhas conferidas', alinhamento: 'numero', celula: (a) => formatarInteiro(a.leituras) },
    { chave: 'lidos', rotulo: 'Campos lidos', alinhamento: 'numero', celula: (a) => formatarInteiro(a.camposLidos) },
    { chave: 'corrigidos', rotulo: 'Corrigidos', alinhamento: 'numero', celula: (a) => formatarInteiro(a.camposCorrigidos) },
    {
      chave: 'taxa',
      rotulo: 'Acerto',
      alinhamento: 'numero',
      /* "95,5%", com o sinal em cinza (p.12) — e não "95,5 %". */
      celula: (a) =>
        a.taxaDeAcerto === null ? (
          '—'
        ) : (
          <Medida
            valor={(a.taxaDeAcerto * 100).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}
            unidade="%"
            colado
          />
        ),
    },
  ];
  return (
    <section className="flex flex-col gap-2">
      <h2 className="txt-secao text-tinta">Quanto a leitura acerta, por instrumento?</h2>
      <p className="txt-apoio text-tinta-secundaria">
        Campos que a conferência não precisou corrigir, sobre os campos lidos, só de folha conferida. É o número que diz se
        o lápis serve.
      </p>
      {carregando ? (
        /* Uma linha por instrumento, e não as oito da `Tabela` carregando: a
           resposta tem no máximo três, e o esqueleto tem a altura dela (p.32).
           Sem isto, a tabela dizia "Nenhuma folha conferida ainda" antes da
           resposta. */
        <QuadroTabela>
          <EsqueletoTabela
            rotulo="Taxa de acerto da leitura por instrumento"
            colunas={colunas}
            linhas={Object.keys(ROTULO_DO_INSTRUMENTO).length}
          />
        </QuadroTabela>
      ) : (
        <Tabela
          rotulo="Taxa de acerto da leitura por instrumento"
          colunas={colunas}
          linhas={acerto}
          /* Uma linha por instrumento, no máximo três: contagem sem paginação. */
          rodape={<RodapeDeContagem total={acerto.length} />}
          chaveDaLinha={(a) => a.instrumento}
          vazio={
            <EstadoVazio
              titulo="Nenhuma folha conferida ainda"
              descricao="A taxa aparece depois da primeira conferência, separada por caneta e lápis."
            />
          }
        />
      )}
    </section>
  );
}

function EmitirFolha({ aoFechar, aoEmitir }: { readonly aoFechar: () => void; readonly aoEmitir: (f: FolhaDaApi) => void }) {
  const base = useId();
  const cliente = useQueryClient();
  const cadastros = useQuery({ queryKey: ['combustivel', 'cadastros-da-folha'], queryFn: () => api.cadastros() });
  const comVisualDaFrota = useOpcoesComVisualDaFrota();
  const inicial = mesCorrente(new Date());
  const [equipamentoId, setEquipamentoId] = useState('');
  const [tanqueId, setTanqueId] = useState('');
  const [de, setDe] = useState(inicial.de);
  const [ate, setAte] = useState(inicial.ate);
  const emitir = useMutation({
    mutationFn: () => apiDaFolha.emitir({ equipamentoId, tanqueId, de, ate }),
    meta: {
      retorno: retornoDe<FolhaDaApi>((f) => ({
        titulo: 'Folha emitida',
        alvo: { codigo: f.equipamentoCodigo, nome: f.equipamentoDescricao },
        fatos: [`${formatarDia(f.de)} a ${formatarDia(f.ate)}`, `${f.tanqueCodigo} · ${f.produtoNome}`],
        codigo: f.codigo,
      })),
      silenciarFalha: true,
    },
    onSuccess: (f) => {
      void cliente.invalidateQueries({ queryKey: CHAVE });
      aoEmitir(f);
    },
  });
  function enviar(evento: FormEvent) {
    evento.preventDefault();
    emitir.mutate();
  }
  const tanques = (cadastros.data?.tanques ?? []).filter((t) => t.ativo);
  return (
    /* Grade da R01 (p.28), limitada a duas colunas: a janela tem 480-640 px,
       e a `formulario-grade` mede a tela, não a janela. */
    <form id={`${base}-formulario`} className="formulario-grade" onSubmit={enviar}>
      {/* A recusa no TOPO do formulário (p.22). */}
      {emitir.isError ? (
        <p role="alert" className="campo-linha txt-apoio text-parada">{`${explicarFalha(emitir.error).erro}. ${explicarFalha(emitir.error).comoResolver}`}</p>
      ) : null}
      {/* Enquanto as listas vêm, o rótulo fica e o controle é esqueleto (p.32):
          uma lista com só "Escolha a máquina" deixava escolher o nada. */}
      <div className="campo-largo min-w-0">
        {cadastros.isPending ? (
          <Campo id={`${base}-maquina`} rotulo="Máquina">
            <ControleCarregando oQue="Carregando a frota" />
          </Campo>
        ) : (
          <CampoSelecao
            rotulo="Máquina"
            valor={equipamentoId}
            rotuloVazio="Escolha a máquina"
            opcoes={comVisualDaFrota((cadastros.data?.equipamentos ?? []).map((e) => opcaoDaMaquina(e)))}
            aoMudar={setEquipamentoId}
            adesivo
          />
        )}
      </div>
      <div className="campo-largo min-w-0">
        {cadastros.isPending ? (
          <Campo id={`${base}-tanque`} rotulo="Tanque ou comboio que abastece">
            <ControleCarregando oQue="Carregando os tanques" />
          </Campo>
        ) : (
          <CampoSelecao
            rotulo="Tanque ou comboio que abastece"
            valor={tanqueId}
            rotuloVazio="Escolha o tanque"
            opcoes={tanques.map((t) => ({ valor: t.id, rotulo: `${t.codigo} — ${t.nome} (${t.produto})` }))}
            aoMudar={setTanqueId}
          />
        )}
      </div>
      <Campo id={`${base}-de`} rotulo="Do dia">
        <input id={`${base}-de`} type="date" className="controle w-full" value={de} onChange={(e) => { setDe(e.target.value); }} />
      </Campo>
      <Campo id={`${base}-ate`} rotulo="Até o dia" ajuda="Uma folha por mês cabe numa página.">
        <input id={`${base}-ate`} type="date" className="controle w-full" value={ate} onChange={(e) => { setAte(e.target.value); }} />
      </Campo>
      {/* Dentro da janela, a ação dela é a laranja (a janela bloqueia a página
          atrás); a saída é terciária à esquerda (p.15), como em todas as
          janelas do módulo — no rodapé fixo, como no "Emitir folha,
          corrigido" da R01 (p.29). */}
      <AcoesNoRodape>
        <Botao nivel="terciario" onClick={aoFechar}>
          Voltar sem emitir
        </Botao>
        <Botao
          nivel="primario"
          type="submit"
          form={`${base}-formulario`}
          disabled={emitir.isPending || equipamentoId === '' || tanqueId === ''}
        >
          {emitir.isPending ? 'Emitindo…' : 'Gravar folha'}
        </Botao>
      </AcoesNoRodape>
    </form>
  );
}

function PainelDaFolha({
  folha,
  leituraLigada,
  aoConferir,
  aoFechar,
}: {
  readonly folha: FolhaDaApi;
  readonly leituraLigada: boolean;
  readonly aoConferir: (leituraId: string) => void;
  readonly aoFechar: () => void;
}) {
  const detalhe = useQuery({ queryKey: [...CHAVE, 'folha', folha.id], queryFn: () => apiDaFolha.folha(folha.id) });
  return (
    <PainelDetalhe
      titulo={`Folha ${folha.codigo}`}
      subtitulo={`${folha.equipamentoCodigo} · ${dataCurta(folha.de)} a ${dataCurta(folha.ate)}`}
      dadosChave={
        <>
          <DadoChave rotulo="Máquina">{folha.equipamentoDescricao}</DadoChave>
          <DadoChave rotulo="Tanque">{`${folha.tanqueNome} · ${folha.produtoNome}`}</DadoChave>
          <DadoChave rotulo="Emitida">{formatarData(new Date(folha.emitidaEm))}</DadoChave>
          <DadoChave rotulo="Lançados">
            <span className="tabular-nums">{formatarInteiro(folha.lancados)}</span>
          </DadoChave>
        </>
      }
      abas={[
        {
          id: 'ler',
          rotulo: 'Ler escaneamento',
          conteudo: leituraLigada ? (
            <Escanear folhaId={folha.id} aoLer={aoConferir} />
          ) : (
            <p className="txt-apoio text-tinta-secundaria">A leitura pela IA está desligada neste ambiente. Lance pelo formulário.</p>
          ),
        },
        {
          id: 'leituras',
          rotulo: `Leituras (${formatarInteiro(folha.leituras)})`,
          conteudo: (
            <ListaDeLeituras
              leituras={detalhe.data?.leituras ?? []}
              carregando={detalhe.isPending}
              aoConferir={aoConferir}
            />
          ),
        },
      ]}
      acoes={[
        <Botao
          key="imprimir"
          nivel="secundario"
          onClick={() => {
            window.open(`/impressao/folha/${folha.id}`, '_blank', 'noopener');
          }}
        >
          Imprimir folha
        </Botao>,
      ]}
      aoFechar={aoFechar}
    />
  );
}

function Escanear({ folhaId, aoLer }: { readonly folhaId: string; readonly aoLer: (leituraId: string) => void }) {
  const base = useId();
  const cliente = useQueryClient();
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [instrumento, setInstrumento] = useState<Instrumento | ''>('');
  const ler = useMutation({
    mutationFn: () => apiDaFolha.escanear(folhaId, instrumento as Instrumento, arquivo!),
    meta: {
      retorno: retornoDe<Awaited<ReturnType<typeof apiDaFolha.escanear>>>((r) => ({
        titulo: 'Folha lida',
        tom: r.codigoConfere === false ? 'ressalva' : 'sucesso',
        ...(r.codigoLido ? { alvo: { codigo: r.codigoLido } } : {}),
        fatos: [
          contagem(r.leitura.linhasLidas.length, 'linha lida', 'linhas lidas'),
          r.codigoConfere === false ? 'O código lido não é o desta folha' : null,
        ],
        efeito: 'Nada entra antes de você conferir as linhas',
      })),
    },
    onSuccess: (r) => {
      void cliente.invalidateQueries({ queryKey: CHAVE });
      aoLer(r.leitura.id);
    },
  });
  return (
    <div className="flex flex-col gap-4">
      <p className="txt-apoio text-tinta-secundaria">
        Envie a folha escaneada ou fotografada, reta e inteira. A IA lê as linhas e marca onde teve dúvida; você confere
        olhando o papel, e só então os abastecimentos entram.
      </p>
      <Campo id={`${base}-arquivo`} rotulo="Imagem da folha" ajuda="PDF, PNG ou JPEG.">
        <input
          id={`${base}-arquivo`}
          type="file"
          accept="application/pdf,image/png,image/jpeg"
          className="controle w-full"
          onChange={(e) => {
            setArquivo(e.target.files?.[0] ?? null);
          }}
        />
      </Campo>
      <CampoSelecao
        rotulo="Com que a folha foi preenchida"
        valor={instrumento}
        rotuloVazio="Escolha"
        opcoes={(Object.keys(ROTULO_DO_INSTRUMENTO) as Instrumento[]).map((i) => ({ valor: i, rotulo: ROTULO_DO_INSTRUMENTO[i] }))}
        aoMudar={(v) => {
          setInstrumento(v as Instrumento | '');
        }}
      />
      {ler.isError ? (
        <p role="alert" className="txt-apoio text-parada">{`${explicarFalha(ler.error).erro}. ${explicarFalha(ler.error).comoResolver}`}</p>
      ) : null}
      <div>
        <HistoriaNaMesa codigo="BTN-CMB-10" className="inline-flex">
          <Botao
            nivel="secundario"
            disabled={ler.isPending || arquivo === null || instrumento === ''}
            onClick={() => {
              ler.mutate();
            }}
          >
            {ler.isPending ? 'Lendo a folha…' : 'Ler e conferir'}
          </Botao>
        </HistoriaNaMesa>
      </div>
      {/* A IA lendo (p.32): esqueleto na forma do que ela devolve — as linhas
          do papel, número, data e hora, litros. A conferência abre sozinha
          quando a leitura termina. */}
      {ler.isPending ? (
        <Carregando oQue="Lendo a folha" className="flex flex-col">
          {['w-1/2', 'w-2/5', 'w-3/5'].map((largura) => (
            <div
              key={largura}
              className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-x-3 border-t border-borda-sutil py-3 first:border-t-0"
            >
              <Barra className="h-3 w-8" />
              <Barra className={`h-3 ${largura}`} />
              <Barra className="h-3 w-12" />
            </div>
          ))}
        </Carregando>
      ) : null}
    </div>
  );
}

function ListaDeLeituras({
  leituras,
  carregando,
  aoConferir,
}: {
  readonly leituras: readonly LeituraDaApi[];
  readonly carregando: boolean;
  readonly aoConferir: (leituraId: string) => void;
}) {
  /* Carregando (p.32) na forma da lista: duas linhas de texto e o botão de
     conferir à direita — nunca a frase solta. */
  if (carregando) {
    return (
      <Carregando oQue="Carregando as leituras da folha" className="flex flex-col">
        {['w-1/2', 'w-2/5', 'w-3/5'].map((largura) => (
          <div key={largura} className="flex items-center justify-between gap-2 border-b border-borda-sutil py-2">
            <span className="flex min-w-0 flex-1 flex-col gap-2 py-1">
              <Barra className={`h-3 ${largura}`} />
              <Barra className="h-3 w-3/4" />
            </span>
            <Barra className="h-[var(--controle-m)] w-32 rounded-controle" />
          </div>
        ))}
      </Carregando>
    );
  }
  if (leituras.length === 0) return <p className="txt-apoio text-tinta-secundaria">Nenhuma leitura desta folha ainda.</p>;
  return (
    <ul className="flex flex-col">
      {leituras.map((l) => (
        <li key={l.id} className="flex items-center justify-between gap-2 border-b border-borda-sutil py-2">
          <span className="flex flex-col">
            <span className="txt-corpo text-tinta">{`${formatarDataHora(new Date(l.lidaEm))} · ${ROTULO_DO_INSTRUMENTO[l.instrumento]}`}</span>
            <span className="txt-apoio text-tinta-secundaria">
              {l.conferidaEm
                ? `Conferida: ${contagem(l.abastecimentosLancados ?? 0, 'abastecimento lançado', 'abastecimentos lançados')}, ${formatarInteiro(l.camposCorrigidos ?? 0)} de ${contagem(l.camposLidos ?? 0, 'campo corrigido', 'campos corrigidos')}`
                : `${contagem(l.linhasLidas.length, 'linha lida', 'linhas lidas')}, falta conferir`}
            </span>
          </span>
          {l.conferidaEm ? null : (
            <Botao
              nivel="terciario"
              onClick={() => {
                aoConferir(l.id);
              }}
            >
              Conferir leitura
            </Botao>
          )}
        </li>
      ))}
    </ul>
  );
}

/**
 * A CONFERÊNCIA (decisão 12): o papel de um lado, o que a IA leu do outro.
 * O campo em que a leitura teve dúvida vem marcado com a palavra "conferir" —
 * nunca só com cor. Linha que a IA inventou se desmarca; linha que ela não
 * viu se acrescenta. O botão lança tudo de uma vez, pela mesma regra do
 * formulário, e a conferência não se refaz.
 */
function ConferenciaDaLeitura({
  folhaId,
  leituraId,
  aoTerminar,
}: {
  readonly folhaId: string;
  readonly leituraId: string;
  readonly aoTerminar: () => void;
}) {
  const cliente = useQueryClient();
  const detalhe = useQuery({ queryKey: [...CHAVE, 'folha', folhaId], queryFn: () => apiDaFolha.folha(folhaId) });
  const imagem = useQuery({ queryKey: [...CHAVE, 'imagem', leituraId], queryFn: () => apiDaFolha.imagem(leituraId), staleTime: 240_000 });
  const leitura = detalhe.data?.leituras.find((l) => l.id === leituraId) ?? null;
  const [linhas, setLinhas] = useState<LinhaEmConferencia[] | null>(null);
  const [recusa, setRecusa] = useState<string | null>(null);
  const atuais = linhas ?? (leitura ? linhasParaConferir(leitura.linhasLidas) : []);
  const lancar = useMutation({
    mutationFn: (envio: Parameters<typeof apiDaFolha.conferir>[1]) => apiDaFolha.conferir(leituraId, envio),
    /* O retorno é toast (p.30, p.31) com o que entrou, e a tela volta à lista
       — antes ela era trocada inteira por um cartão de status. */
    meta: { silencioso: true },
    onSuccess: (r) => {
      void cliente.invalidateQueries({ queryKey: ['combustivel'] });
      const codigo = detalhe.data?.folha.codigo;
      anunciar({
        tom: 'sucesso',
        titulo: 'Folha conferida e lançada',
        ...(codigo === undefined ? {} : { alvo: { codigo, nome: detalhe.data?.folha.equipamentoCodigo ?? '' } }),
        fatos: [
          contagem(r.lancados, 'abastecimento lançado', 'abastecimentos lançados'),
          `a leitura acertou ${formatarInteiro(r.camposLidos - r.camposCorrigidos)} de ${contagem(r.camposLidos, 'campo', 'campos')}`,
        ],
      });
      aoTerminar();
    },
  });

  if (detalhe.isPending) return <EsqueletoDaConferencia aoTerminar={aoTerminar} />;
  if (detalhe.isError || !detalhe.data || !leitura) {
    return (
      <EstadoVazio
        titulo="Não foi possível abrir a leitura"
        descricao={detalhe.isError ? explicarFalha(detalhe.error).comoResolver : 'Volte às folhas e abra a leitura de novo.'}
        acao={
          <Botao nivel="secundario" onClick={aoTerminar}>
            Voltar às folhas
          </Botao>
        }
      />
    );
  }
  const folha = detalhe.data.folha;
  const pessoas = detalhe.data.pessoas;
  const incluidas = atuais.filter((l) => l.incluir).length;
  const mudar = (chave: string, campo: Partial<LinhaEmConferencia>) => {
    setLinhas(atuais.map((l) => (l.chave === chave ? { ...l, ...campo } : l)));
  };

  return (
    <>
      <CabecalhoPagina
        sobretitulo={`Folha ${folha.codigo} · ${ROTULO_DO_INSTRUMENTO[leitura.instrumento]}`}
        titulo="Conferir a leitura"
        subtitulo={`${folha.equipamentoCodigo} — ${folha.equipamentoDescricao} · ${folha.tanqueNome} · ${dataCurta(folha.de)} a ${dataCurta(folha.ate)}. Olhe o papel e corrija o que a IA leu errado.`}
        acoes={
          <>
            <Botao nivel="secundario" onClick={aoTerminar}>
              Voltar sem lançar
            </Botao>
            <Botao
              nivel="primario"
              disabled={lancar.isPending || leitura.conferidaEm !== null}
              onClick={() => {
                const r = montarEnvio(atuais, folha);
                if ('recusa' in r) {
                  setRecusa(r.recusa);
                  return;
                }
                setRecusa(null);
                lancar.mutate(r.linhas);
              }}
            >
              {lancar.isPending ? 'Lançando…' : `Lançar ${contagem(incluidas, 'abastecimento', 'abastecimentos')}`}
            </Botao>
          </>
        }
      />
      {recusa ? <p role="alert" className="txt-corpo text-parada">{recusa}</p> : null}
      {lancar.isError ? (
        <p role="alert" className="txt-corpo text-parada">{`${explicarFalha(lancar.error).erro}. ${explicarFalha(lancar.error).comoResolver}`}</p>
      ) : null}

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)]">
        <section className="flex flex-col gap-2">
          <h2 className="txt-secao text-tinta">A folha escaneada</h2>
          {imagem.data && imagem.data.mime !== 'application/pdf' ? (
            <img src={imagem.data.url} alt={`Folha ${folha.codigo} escaneada`} className="w-full rounded-controle border border-borda-sutil" />
          ) : imagem.data ? (
            <a className="txt-corpo text-marca-texto underline" href={imagem.data.url} target="_blank" rel="noreferrer">
              Abrir a folha escaneada (PDF)
            </a>
          ) : imagem.isError ? (
            <p className="txt-apoio text-tinta-secundaria">A imagem não abriu. Confira pelo papel.</p>
          ) : (
            /* A imagem vindo (p.32): o bloco na proporção da folha impressa,
               em pé, para a coluna não pular quando ela chega. */
            <Carregando oQue="Carregando a folha escaneada">
              <Barra className="aspect-[210/297] w-full rounded-controle" />
            </Carregando>
          )}
        </section>

        <section className="flex flex-col gap-3">
          <h2 className="txt-secao text-tinta">O que a IA leu</h2>
          <p className="txt-apoio text-tinta-secundaria">
            Campo marcado com “conferir” foi lido com dúvida. Desmarque a linha que não existe no papel; acrescente a que a
            leitura não viu.
          </p>
          {/* O desenho da `Tabela` (p.18), numa grade que se edita: quadro com
              contorno, cabeçalho rebaixado, divisória de 1 px e a PRIMEIRA
              COLUNA fixa ao rolar de lado — a linha do papel, com a caixa de
              lançar dentro dela. A altura é a de linha dupla; a linha cresce
              só quando a leitura marcou "conferir" debaixo do campo. */}
          <div className="cartao overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full border-separate border-spacing-0" aria-label="Linhas lidas pela IA">
                <thead>
                  <tr className="h-[var(--tabela-cabecalho)] bg-rebaixada">
                    <th scope="col" className="txt-legenda sticky left-0 z-10 bg-rebaixada px-4 text-left text-tinta-secundaria">
                      Linha
                    </th>
                    <th scope="col" className="txt-legenda px-4 text-left text-tinta-secundaria">Data</th>
                    <th scope="col" className="txt-legenda px-4 text-left text-tinta-secundaria">Hora</th>
                    <th scope="col" className="txt-legenda px-4 text-right text-tinta-secundaria">
                      {folha.tipoMedidor === 'odometro' ? 'Odômetro' : 'Horímetro'}
                    </th>
                    <th scope="col" className="txt-legenda px-4 text-right text-tinta-secundaria">Litros</th>
                    <th scope="col" className="txt-legenda px-4 text-left text-tinta-secundaria">Operador</th>
                  </tr>
                </thead>
                <tbody>
                  {atuais.map((l, i) => (
                    <LinhaDaConferencia key={l.chave} linha={l} indice={i} pessoas={pessoas} aoMudar={(c) => { mudar(l.chave, c); }} />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          {atuais.length === 0 ? (
            <p className="txt-apoio text-tinta-secundaria">A leitura não achou linha preenchida. Acrescente as linhas do papel.</p>
          ) : null}
          <div>
            <Botao
              nivel="terciario"
              onClick={() => {
                setLinhas([
                  ...atuais,
                  { chave: `nova-${String(atuais.length + 1)}`, origem: null, incluir: true, data: '', hora: '', medidor: '', litros: '', operadorId: '', duvidas: [], operadorEscrito: null },
                ]);
              }}
            >
              Acrescentar linha
            </Botao>
          </div>
        </section>
      </div>
    </>
  );
}

/**
 * A conferência enquanto a leitura chega (p.32): o título, o "Voltar" e os
 * nomes das duas metades não dependem da resposta e aparecem; a folha, o
 * cabeçalho com a máquina e as linhas lidas viram esqueleto, cada um na forma
 * dele. O medidor é "Medidor" até a folha dizer se é horímetro ou odômetro.
 */
function EsqueletoDaConferencia({ aoTerminar }: { readonly aoTerminar: () => void }) {
  return (
    <>
      <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-4">
        <div className="flex min-w-0 flex-[1_1_320px] flex-col gap-1">
          <Barra className="my-1 h-2 w-40" />
          <h1 className="txt-pagina text-tinta">Conferir a leitura</h1>
          <Barra className="my-1 h-3 w-3/4" />
        </div>
        <div className="ml-auto flex max-w-full flex-wrap items-center justify-end gap-2">
          <Botao nivel="secundario" onClick={aoTerminar}>
            Voltar sem lançar
          </Botao>
          <Barra className="h-[var(--controle-m)] w-48 rounded-controle" />
        </div>
      </div>

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)]">
        <section className="flex flex-col gap-2">
          <h2 className="txt-secao text-tinta">A folha escaneada</h2>
          <Barra className="aspect-[210/297] w-full rounded-controle" />
        </section>

        <section className="flex flex-col gap-3">
          <h2 className="txt-secao text-tinta">O que a IA leu</h2>
          <span className="flex flex-col gap-2 py-1">
            <Barra className="h-3 w-full" />
            <Barra className="h-3 w-3/5" />
          </span>
          <div className="cartao overflow-hidden">
            <EsqueletoTabela
              /* Minúsculo de propósito: vira "Carregando a leitura da folha". */
              rotulo="a leitura da folha"
              colunas={[
                { chave: 'linha', rotulo: 'Linha' },
                { chave: 'data', rotulo: 'Data' },
                { chave: 'hora', rotulo: 'Hora' },
                { chave: 'medidor', rotulo: 'Medidor', alinhamento: 'numero' },
                { chave: 'litros', rotulo: 'Litros', alinhamento: 'numero' },
                { chave: 'operador', rotulo: 'Operador' },
              ]}
              linhas={5}
            />
          </div>
        </section>
      </div>
    </>
  );
}

function LinhaDaConferencia({
  linha,
  indice,
  pessoas,
  aoMudar,
}: {
  readonly linha: LinhaEmConferencia;
  readonly indice: number;
  readonly pessoas: readonly { id: string; nome: string }[];
  readonly aoMudar: (campo: Partial<LinhaEmConferencia>) => void;
}) {
  const base = useId();
  const duvida = (c: CampoDaLinha) => linha.duvidas.includes(c);
  const marca = (c: CampoDaLinha) =>
    duvida(c) ? (
      <span id={`${base}-${c}-duvida`} className="txt-apoio text-tinta">
        conferir
      </span>
    ) : null;
  const propsDoCampo = (c: CampoDaLinha) => ({
    id: `${base}-${c}`,
    'aria-label': `${ROTULO_DO_CAMPO[c]} da linha ${String(linha.origem ?? indice + 1)}`,
    ...(duvida(c) ? { 'aria-describedby': `${base}-${c}-duvida` } : {}),
    disabled: !linha.incluir,
  });
  const celula = 'border-t border-borda-sutil px-4 py-1';
  return (
    <tr className={`h-[var(--linha-tabela)] ${linha.incluir ? '' : 'text-tinta-apagada'}`}>
      <td className={`${celula} sticky left-0 bg-card`}>
        <label className="flex items-center gap-3">
          <input
            type="checkbox"
            className="size-4 shrink-0"
            aria-label={`Lançar a linha ${String(linha.origem ?? indice + 1)}`}
            checked={linha.incluir}
            onChange={(e) => {
              aoMudar({ incluir: e.target.checked });
            }}
          />
          <span className="txt-corpo tabular-nums">{linha.origem === null ? 'nova' : String(linha.origem)}</span>
        </label>
      </td>
      <td className={celula}>
        <div className="flex flex-col gap-1">
          <input {...propsDoCampo('data')} type="date" className="controle" value={linha.data} onChange={(e) => { aoMudar({ data: e.target.value }); }} />
          {marca('data')}
        </div>
      </td>
      <td className={celula}>
        <div className="flex flex-col gap-1">
          <input {...propsDoCampo('hora')} type="time" className="controle" value={linha.hora} onChange={(e) => { aoMudar({ hora: e.target.value }); }} />
          {marca('hora')}
        </div>
      </td>
      <td className={celula}>
        <div className="flex flex-col items-end gap-1">
          <input {...propsDoCampo('medidor')} inputMode="decimal" className="controle w-28 text-right tabular-nums" value={linha.medidor} onChange={(e) => { aoMudar({ medidor: e.target.value }); }} />
          {marca('medidor')}
        </div>
      </td>
      <td className={celula}>
        <div className="flex flex-col items-end gap-1">
          <input {...propsDoCampo('litros')} inputMode="decimal" className="controle w-24 text-right tabular-nums" value={linha.litros} onChange={(e) => { aoMudar({ litros: e.target.value }); }} />
          {marca('litros')}
        </div>
      </td>
      <td className={celula}>
        <div className="flex flex-col gap-1">
          <select {...propsDoCampo('operador')} className="controle" value={linha.operadorId} onChange={(e) => { aoMudar({ operadorId: e.target.value }); }}>
            <option value="">Sem operador</option>
            {pessoas.map((p) => (
              <option key={p.id} value={p.id}>
                {p.nome}
              </option>
            ))}
          </select>
          {linha.operadorEscrito ? <span className="txt-apoio text-tinta-secundaria">{`No papel: ${linha.operadorEscrito}`}</span> : null}
          {marca('operador')}
        </div>
      </td>
    </tr>
  );
}
